"use client";

import { useCallback, useSyncExternalStore } from "react";
import type { Id } from "@convex/_generated/dataModel";
import { isConvexId } from "@/lib/convex/validate-id";
import { isUnknownRecord } from "@shared/validation/narrow";

const STORAGE_KEY = "repackify_cart";
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export interface GuestCartItem {
  productId: Id<"products">;
  quantity: number;
  unitPriceCents: number;
  productTitle: string;
  productTypeSlug: string;
  addedAt: number;
  priceVersion?: "buyer";
}

function isValidGuestCartItem(item: unknown): item is GuestCartItem {
  if (!isUnknownRecord(item)) return false;
  const obj = item;
  return (
    isConvexId<"products">(obj.productId) &&
    typeof obj.quantity === "number" &&
    Number.isFinite(obj.quantity) &&
    typeof obj.unitPriceCents === "number" &&
    typeof obj.productTitle === "string" &&
    typeof obj.productTypeSlug === "string" &&
    typeof obj.addedAt === "number"
  );
}

function readItems(): GuestCartItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const now = Date.now();
    return parsed
      .filter(isValidGuestCartItem)
      .filter((item) => now - item.addedAt < MAX_AGE_MS);
  } catch {
    return [];
  }
}

function writeItems(items: GuestCartItem[]) {
  if (typeof window === "undefined") return;
  try {
    if (items.length === 0) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }
  } catch {}
  notifyListeners();
}

let listeners: Array<() => void> = [];
let cachedSnapshot: GuestCartItem[] = [];
let snapshotDirty = true;

function handleStorageEvent(event: StorageEvent) {
  if (event.key === STORAGE_KEY || event.key === null) {
    notifyListeners();
  }
}

function subscribe(listener: () => void) {
  if (listeners.length === 0 && typeof window !== "undefined") {
    window.addEventListener("storage", handleStorageEvent);
  }
  listeners.push(listener);
  return () => {
    listeners = listeners.filter((existing) => existing !== listener);
    if (listeners.length === 0 && typeof window !== "undefined") {
      window.removeEventListener("storage", handleStorageEvent);
    }
  };
}

function notifyListeners() {
  snapshotDirty = true;
  for (const listener of listeners) {
    listener();
  }
}

const EMPTY_ITEMS: GuestCartItem[] = [];

function updateItemQuantity(productId: string, quantity: number) {
  if (!Number.isInteger(quantity) || quantity < 1) return;
  const current = readItems();
  const item = current.find((i) => i.productId === productId);
  if (item) {
    item.quantity = quantity;
    writeItems(current);
  }
}

function getSnapshot(): GuestCartItem[] {
  if (snapshotDirty) {
    cachedSnapshot = readItems();
    snapshotDirty = false;
  }
  return cachedSnapshot;
}

function getServerSnapshot(): GuestCartItem[] {
  return EMPTY_ITEMS;
}

function addGuestCartItem(item: Omit<GuestCartItem, "addedAt">): void {
  if (!Number.isInteger(item.quantity) || item.quantity < 1) return;
  const current = readItems();
  const existingIndex = current.findIndex(
    (cartItem) => cartItem.productId === item.productId,
  );

  const entry: GuestCartItem = {
    ...item,
    addedAt: Date.now(),
    priceVersion: "buyer",
  };

  if (existingIndex >= 0) {
    current[existingIndex] = entry;
  } else {
    current.push(entry);
  }

  writeItems(current);
}

export function useGuestCart() {
  const items = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const addItem = useCallback(
    (item: Omit<GuestCartItem, "addedAt">) => addGuestCartItem(item),
    [],
  );

  const updateQuantity = useCallback(
    (productId: string, quantity: number) =>
      updateItemQuantity(productId, quantity),
    [],
  );

  const removeItem = useCallback((productId: string) => {
    const current = readItems();
    writeItems(current.filter((i) => i.productId !== productId));
  }, []);

  const clearAll = useCallback(() => {
    writeItems([]);
  }, []);

  const getItem = useCallback(
    (productId: string) => {
      return items.find((i) => i.productId === productId) ?? null;
    },
    [items],
  );

  return {
    items,
    itemCount: items.length,
    addItem,
    updateQuantity,
    removeItem,
    clearAll,
    getItem,
  };
}

function clearGuestCart() {
  writeItems([]);
}

const CHECKOUT_ROWS_PREFIX = "repackify_checkout_rows:";

export interface CheckoutRowKey {
  productId: string;
  addedAt: number;
}

function checkoutRowsNow(): CheckoutRowKey[] {
  return readItems().map(({ productId, addedAt }) => ({ productId, addedAt }));
}

function rememberCheckoutRows(attemptId: string, rows: CheckoutRowKey[]): void {
  try {
    localStorage.setItem(
      `${CHECKOUT_ROWS_PREFIX}${attemptId}`,
      JSON.stringify(rows),
    );
  } catch {}
}

function readCheckoutRows(attemptId: string): CheckoutRowKey[] | null {
  try {
    const raw = localStorage.getItem(`${CHECKOUT_ROWS_PREFIX}${attemptId}`);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.filter(
      (row): row is CheckoutRowKey =>
        isUnknownRecord(row) &&
        typeof row.productId === "string" &&
        typeof row.addedAt === "number",
    );
  } catch {
    return null;
  }
}

function wasAtCheckout(item: GuestCartItem, rows: CheckoutRowKey[]) {
  return rows.some(
    (row) => row.productId === item.productId && row.addedAt === item.addedAt,
  );
}

function removeGuestCartItems(
  purchased: Array<{ productId: string; quantity: number }>,
  rowsAtCheckout: CheckoutRowKey[] | null,
) {
  if (rowsAtCheckout === null) return;
  const purchasedQuantity = new Map(
    purchased.map((entry) => [entry.productId, entry.quantity]),
  );
  writeItems(
    readItems().flatMap((item) => {
      if (!wasAtCheckout(item, rowsAtCheckout)) return [item];
      const remaining =
        item.quantity - (purchasedQuantity.get(item.productId) ?? 0);
      return remaining > 0 ? [{ ...item, quantity: remaining }] : [];
    }),
  );
}

export {
  readItems as readGuestCartItems,
  clearGuestCart,
  removeGuestCartItems,
  rememberCheckoutRows,
  readCheckoutRows,
  checkoutRowsNow,
};
